import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Badge,
} from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import DataTable, { Column, DateCell } from '../../../components/Common/DataTable';
import api from '../../../services/api';

interface CniVerification {
  id: number;
  user_id: string;
  cni_number: string;
  full_name: string;
  birth_date: string;
  verification_status: 'PENDING' | 'VERIFIED' | 'FAILED' | 'EXPIRED' | 'REJECTED';
  failure_reason: string | null;
  verified_at: string | null;
  expires_at: string | null;
  is_sandbox: boolean;
  created_at: string;
}

interface Stats {
  total: number;
  verified: number;
  pending: number;
  failed: number;
}

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  VERIFIED: 'success',
  PENDING: 'warning',
  FAILED: 'error',
  EXPIRED: 'default',
  REJECTED: 'error',
};

const CniVerificationsPage: React.FC = () => {
  const [verifications, setVerifications] = useState<CniVerification[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, verified: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState<CniVerification | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchVerifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/cni-verifications');
      if (response.data?.success) {
        const data = response.data.data || [];
        setVerifications(data);
        setStats({
          total: data.length,
          verified: data.filter((v: CniVerification) => v.verification_status === 'VERIFIED').length,
          pending: data.filter((v: CniVerification) => v.verification_status === 'PENDING').length,
          failed: data.filter((v: CniVerification) => v.verification_status === 'FAILED').length,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar verificações CNI:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  const handleViewDetail = (verification: CniVerification) => {
    setSelectedVerification(verification);
    setDetailOpen(true);
  };

  const handleUpdateStatus = async (id: number, newStatus: string, reason?: string) => {
    try {
      await api.put(`/admin/cni-verifications/${id}`, {
        verification_status: newStatus,
        failure_reason: reason,
      });
      fetchVerifications();
      setDetailOpen(false);
    } catch (error) {
      console.error('Erro ao atualizar verificação:', error);
    }
  };

  const columns: Column<CniVerification>[] = [
    {
      id: 'cni_number',
      label: 'CNI',
      format: (_value, row) => (
        <Typography variant="body2" fontWeight="bold" fontFamily="monospace">
          {row.cni_number}
        </Typography>
      ),
    },
    { id: 'full_name', label: 'Nome' },
    {
      id: 'verification_status',
      label: 'Estado',
      format: (value) => (
        <Chip
          label={value}
          color={statusColors[value as string] || 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'is_sandbox',
      label: 'Ambiente',
      format: (value) => (
        <Chip
          label={value ? 'Sandbox' : 'Produção'}
          variant="outlined"
          size="small"
          color={value ? 'info' : 'default'}
        />
      ),
    },
    {
      id: 'created_at',
      label: 'Data',
      format: (value) => <DateCell value={value} />,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            <Badge sx={{ mr: 1, verticalAlign: 'middle' }} />
            Verificações CNI
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestão de verificações de identidade via CNI
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchVerifications}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
              <Typography variant="body2" color="text.secondary">Total</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">{stats.verified}</Typography>
              <Typography variant="body2" color="text.secondary">Verificados</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.pending}</Typography>
              <Typography variant="body2" color="text.secondary">Pendentes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h4" fontWeight="bold" color="error.main">{stats.failed}</Typography>
              <Typography variant="body2" color="text.secondary">Falhados</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <DataTable
        columns={columns}
        data={verifications}
        loading={loading}
        onView={handleViewDetail}
        emptyMessage="Nenhuma verificação CNI encontrada"
      />

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        {selectedVerification && (
          <>
            <DialogTitle>
              Verificação CNI - {selectedVerification.cni_number}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField label="CNI" value={selectedVerification.cni_number} disabled fullWidth />
                <TextField label="Nome" value={selectedVerification.full_name || '-'} disabled fullWidth />
                <TextField label="Data de Nascimento" value={selectedVerification.birth_date || '-'} disabled fullWidth />
                <TextField label="Estado" value={selectedVerification.verification_status} disabled fullWidth />
                {selectedVerification.failure_reason && (
                  <Alert severity="error">{selectedVerification.failure_reason}</Alert>
                )}
                {selectedVerification.is_sandbox && (
                  <Alert severity="info">Esta verificação foi feita em modo sandbox.</Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              {selectedVerification.verification_status === 'PENDING' && (
                <>
                  <Button
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleUpdateStatus(selectedVerification.id, 'VERIFIED')}
                  >
                    Aprovar
                  </Button>
                  <Button
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleUpdateStatus(selectedVerification.id, 'REJECTED', 'Rejeitado manualmente')}
                  >
                    Rejeitar
                  </Button>
                </>
              )}
              <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CniVerificationsPage;
